import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CalendarOff, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const StaffTimeOffDialog = ({ staff, date, onClose, onSaved }) => {
  const { t } = useTranslation();
  const token = localStorage.getItem('sessionToken');
  const [fullDay, setFullDay] = useState(true);
  const [range, setRange] = useState({ start_time: '16:00', end_time: '20:00' });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflicts, setConflicts] = useState([]);

  const submit = async () => {
    setError('');
    setConflicts([]);
    setSaving(true);
    try {
      await axios.post(`${API}/provider/staff/${staff.id}/days-off`, {
        date,
        is_full_day: fullDay,
        reason,
        ...(fullDay ? {} : range),
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = detail?.error;
      if (code === 'bookings_in_window') {
        setConflicts(detail.affected_appointments || []);
        setError(t('timeOff.hasBookings', 'Move or cancel these bookings first, then mark the time off.'));
      } else if (code === 'overlaps_time_off') {
        setError(t('timeOff.overlapsTimeOff', 'That overlaps time off already set for {{start}}–{{end}}',
          { start: detail.start_time, end: detail.end_time }));
      } else if (code === 'overlaps_break') {
        setError(t('timeOff.overlapsBreak', 'That overlaps the scheduled break ({{start}}–{{end}})',
          { start: detail.start_time, end: detail.end_time }));
      } else if (code === 'already_full_day_off') {
        setError(t('timeOff.alreadyFullDay', 'This staff member is already off for the whole day'));
      } else if (code === 'partial_off_exists') {
        setError(t('timeOff.partialExists', 'Time off is already set for part of this day — remove it first'));
      } else if (code === 'end_before_start') {
        setError(t('timeOff.endBeforeStart', 'The end time must be after the start time'));
      } else {
        setError(typeof detail === 'string' ? detail : t('timeOff.saveFailed', 'Could not save the time off'));
      }
    } finally {
      setSaving(false);
    }
  };

  const field = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 sm:items-center sm:p-4" data-testid="time-off-dialog">
      <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t('timeOff.title', 'Time off')}</h3>
            <p className="numeric-ltr text-xs text-slate-500">{staff.name} · {date}</p>
          </div>
          <button onClick={onClose} data-testid="time-off-close" className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4 flex gap-2">
          <button
            onClick={() => setFullDay(true)}
            data-testid="time-off-full-day"
            className={`flex-1 rounded-full px-3 py-2 text-xs font-bold ${fullDay ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
          >
            {t('timeOff.fullDay', 'Whole day')}
          </button>
          <button
            onClick={() => setFullDay(false)}
            data-testid="time-off-part-day"
            className={`flex-1 rounded-full px-3 py-2 text-xs font-bold ${!fullDay ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
          >
            {t('timeOff.partDay', 'Part of the day')}
          </button>
        </div>

        {!fullDay && (
          <div className="mb-3 grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-slate-600">{t('timeOff.from', 'From')}</label>
              <input type="time" className={`${field} numeric-ltr`} value={range.start_time} data-testid="time-off-from"
                onChange={(e) => setRange({ ...range, start_time: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-slate-600">{t('timeOff.to', 'To')}</label>
              <input type="time" className={`${field} numeric-ltr`} value={range.end_time} data-testid="time-off-to"
                onChange={(e) => setRange({ ...range, end_time: e.target.value })} />
            </div>
          </div>
        )}

        <div className="mb-3">
          <label className="mb-1 block text-xs font-bold text-slate-600">{t('timeOff.reason', 'Reason (optional)')}</label>
          <input className={field} value={reason} data-testid="time-off-reason"
            onChange={(e) => setReason(e.target.value)} />
        </div>

        {error && (
          <p className="mb-2 rounded-lg bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700" data-testid="time-off-error">{error}</p>
        )}
        {conflicts.length > 0 && (
          <ul className="mb-3 space-y-1" data-testid="time-off-conflicts">
            {conflicts.map((c) => (
              <li key={c.appointment_id} className="numeric-ltr rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                {c.time} · {c.patient_name} · {c.service}
              </li>
            ))}
          </ul>
        )}

        <button
          onClick={submit}
          disabled={saving}
          data-testid="time-off-submit"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarOff className="h-4 w-4" />}
          {t('timeOff.submit', 'Mark time off')}
        </button>
      </div>
    </div>
  );
};

export default StaffTimeOffDialog;
